import { Typography as MuiTypography } from '@mui/material';

import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  ConfirmDialog,
} from '@occc-app/design-system';
import { ScanDataWeekEndingOverviewFragment } from '@occc/graphql';

import { ScanDataWeeklyFileOverviewCard } from './scan-data-weekly-file-overview-card.component';

import { FileDisplayStatus } from '../scan-data-weekly-file.constants';

export type ScanDataWeeklyFileOverviewContainerProps = {
  loading: boolean;
  error: Error | null | undefined;
  data: ScanDataWeekEndingOverviewFragment | null | undefined;
  weekEnding: string;
  lastSftpPullDateTime: string | null;
  canSubmit: boolean;
  isSubmitting: boolean;
  onSubmitClick: () => void;
  fileDisplayStatus: FileDisplayStatus;
  onViewStoresClick: () => void;
  isConfirmDialogOpen: boolean;
  setIsConfirmDialogOpen: (open: boolean) => void;
  handleConfirmSubmit: () => Promise<void>;
  isSubmitAllowed: boolean;
};

export const ScanDataWeeklyFileOverviewContainer = ({
  loading,
  error,
  data,
  weekEnding,
  lastSftpPullDateTime,
  canSubmit,
  isSubmitting,
  onSubmitClick,
  fileDisplayStatus,
  onViewStoresClick,
  isConfirmDialogOpen,
  setIsConfirmDialogOpen,
  handleConfirmSubmit,
  isSubmitAllowed,
}: ScanDataWeeklyFileOverviewContainerProps) => {
  if (loading) {
    return (
      <Card sx={{ mb: 1 }}>
        <CardContent>
          <Box display='flex' justifyContent='center' alignItems='center' p={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mb: 1 }}>
        <CardContent>
          <Typography color='error'>
            Something went wrong while loading data. Please try again.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const weekEndPerformance = data?.weekEndPerformance;
  const pastWeekEndPerformance = data?.pastWeekEndPerformance;

  const storesWithTransactions = weekEndPerformance?.storesWithTransactions || 0;
  const missingStores = weekEndPerformance?.missingStores || 0;
  const reportLines = weekEndPerformance?.reportedLines || 0;

  const pastStoresWithTransactions = pastWeekEndPerformance?.storesWithTransactions || 0;
  const pastReportLines = pastWeekEndPerformance?.reportedLines || 0;

  return (
    <>
      <ScanDataWeeklyFileOverviewCard
        weekEnding={weekEnding}
        lastSftpPullDateTime={lastSftpPullDateTime}
        canSubmit={canSubmit}
        isSubmitting={isSubmitting}
        onSubmitClick={onSubmitClick}
        storesWithTransactions={storesWithTransactions}
        pastStoresWithTransactions={pastStoresWithTransactions}
        missingStores={missingStores}
        onViewStoresClick={onViewStoresClick}
        reportLines={reportLines}
        pastReportLines={pastReportLines}
        fileDisplayStatus={fileDisplayStatus}
        isSubmitAllowed={isSubmitAllowed}
      />

      <ConfirmDialog
        dialogTitle='Are you sure you want to submit this weekly file?'
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        confirmButtonText='Accept and Submit'
        confirmButtonProps={{
          onClick: handleConfirmSubmit,
          disabled: isSubmitting,
        }}
        showLoadingOverlay={isSubmitting}
      >
        {(data?.weekEndPerformance?.missingStores || 0) > 0 && (
          <MuiTypography variant='body2' color='warning.main' sx={{ mb: 2 }}>
            There are missing stores currently. Submission of a file without complete data may cause
            a resubmission later.
          </MuiTypography>
        )}
      </ConfirmDialog>
    </>
  );
};
